import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { 
  CreditCard, CheckCircle, AlertTriangle, Clock, 
  Users, ArrowRight, RefreshCw, Receipt, Loader2,
  IndianRupee, Shield, UserPlus, Sparkles, Info, FileText,
  Crown, Mail, ArrowUpRight, Check, Zap, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BillingPage = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [seatCount, setSeatCount] = useState(null);
  const [seatUpdateLoading, setSeatUpdateLoading] = useState(false);
  const [showSeatPayment, setShowSeatPayment] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showPlanFeatures, setShowPlanFeatures] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [downgradeLoading, setDowngradeLoading] = useState(false);
  const [downgradeError, setDowngradeError] = useState(null);
  const [desiredSeats, setDesiredSeats] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    if (sessionId && status === 'success') {
      setPaymentStatus('checking');
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      setPaymentStatus('cancelled');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    if (attempts >= maxAttempts) { setPaymentStatus('timeout'); return; }
    try {
      const response = await fetch(`${API_URL}/api/payments/stripe/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        fetchSubscription();
        fetchTransactions();
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      setPaymentStatus('error');
    }
  };

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSubscription(data);
      if (data.billing_cycle) setBillingCycle(data.billing_cycle);
    } catch (error) {
      console.error('Fetch subscription error:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTransactions(data);
    } catch (error) {
      console.error('Fetch transactions error:', error);
    }
  }, [token]);

  useEffect(() => { fetchSubscription(); fetchTransactions(); }, [fetchSubscription, fetchTransactions]);

  useEffect(() => {
    if (subscription?.billable_users && seatCount === null) {
      setSeatCount(subscription.billable_users);
    }
    if (subscription?.user_count && desiredSeats === null) {
      setDesiredSeats(subscription.user_count || 1);
    }
  }, [subscription, seatCount, desiredSeats]);

  // handleApplyPromo removed — promo codes now managed by Super Admin only

  const handleUpdateSeats = async () => {
    if (!seatCount || seatCount < 1) return;
    const currentBillable = subscription?.billable_users || subscription?.user_count || 0;
    if (seatCount > currentBillable) {
      setShowSeatPayment(true);
    } else if (seatCount < currentBillable) {
      setSeatUpdateLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/payments/update-seats`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ new_seat_count: seatCount })
        });
        const data = await response.json();
        if (!response.ok) { toast.error(data.detail || 'Failed to update seats'); return; }
        fetchSubscription();
        setShowSeatPayment(false);
        toast.success('Seats updated successfully');
      } catch (error) {
        toast.error('Failed to update seat count');
      } finally {
        setSeatUpdateLoading(false);
      }
    }
  };

  const handleDowngradeToFree = async () => {
    setDowngradeLoading(true);
    setDowngradeError(null);
    try {
      const response = await fetch(`${API_URL}/api/payments/downgrade-to-free`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) {
        setDowngradeError(data.detail || 'Failed to downgrade');
        return;
      }
      toast.success('Plan changed to Free (Individual)');
      setShowDowngradeModal(false);
      fetchSubscription();
      // Refresh page after a moment so sidebar updates
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      setDowngradeError('Failed to downgrade plan. Please try again.');
    } finally {
      setDowngradeLoading(false);
    }
  };

  const handleStripeCheckout = async (customSeatCount) => {
    setPaymentLoading(true);
    try {
      const body = { origin_url: window.location.origin, billing_cycle: billingCycle };
      if (customSeatCount) body.seat_count = customSeatCount;
      const response = await fetch(`${API_URL}/api/payments/stripe/checkout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (data.url) { window.location.href = data.url; }
      else { toast.error(data.detail || 'Failed to create checkout session'); }
    } catch (error) {
      toast.error('Payment failed. Please try again.');
    } finally { setPaymentLoading(false); }
  };

  const handleRazorpayCheckout = async (customSeatCount) => {
    setPaymentLoading(true);
    try {
      const body = { billing_cycle: billingCycle };
      if (customSeatCount) body.seat_count = customSeatCount;
      const orderResponse = await fetch(`${API_URL}/api/payments/razorpay/order`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const orderData = await orderResponse.json();
      if (!orderData.order_id) { if (orderData.detail) toast.error(orderData.detail); return; }

      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        await new Promise(resolve => script.onload = resolve);
      }

      const cycleLabel = billingCycle === 'yearly' ? 'year' : 'month';
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        order_id: orderData.order_id,
        name: 'Inspiro Tracker',
        description: `${orderData.user_count} user(s) - ${billingCycle} plan`,
        handler: async (response) => {
          try {
            const verifyResponse = await fetch(`${API_URL}/api/payments/razorpay/verify`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              })
            });
            const verifyData = await verifyResponse.json();
            if (verifyData.success) { setPaymentStatus('success'); fetchSubscription(); fetchTransactions(); }
            else { setPaymentStatus('error'); }
          } catch (error) { setPaymentStatus('error'); }
        },
        theme: { color: '#3B82F6' }
      };
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error('Payment failed. Please try again.');
    } finally { setPaymentLoading(false); }
  };

  // Pricing helpers
  const PRICE_PER_USER = subscription?.price_per_user || 199;
  const effectiveActivePrice = subscription?.effective_price_per_user || PRICE_PER_USER;
  const YEARLY_PER_USER = subscription?.yearly_price_per_user || Math.round(PRICE_PER_USER * 12 * 0.9);
  const effectivePrice = effectiveActivePrice;
  const DISCOUNT_PCT = subscription?.yearly_discount_percent || 10;
  const TRIAL_USER_LIMIT = subscription?.trial_user_limit || 3;
  const promoActiveOnPlan = subscription?.promo_active || false;
  const promoExpiresAt = subscription?.promo_expires_at ? new Date(subscription.promo_expires_at) : null;

  const getDisplayPrice = (seats) => {
    if (billingCycle === 'yearly') return seats * Math.round(effectiveActivePrice * 12 * 0.9);
    return seats * effectiveActivePrice;
  };

  const getCycleLabel = () => billingCycle === 'yearly' ? '/year' : '/month';

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </Layout>
    );
  }

  const isTrialExpired = subscription?.plan === 'trial' && !subscription?.is_valid;
  const isTrialActive = subscription?.plan === 'trial' && subscription?.is_valid;
  const isActive = subscription?.plan === 'active' && subscription?.is_valid;
  const isExpired = (subscription?.plan === 'active' && !subscription?.is_valid) || subscription?.plan === 'expired';
  const isFreePlan = subscription?.plan === 'free';
  const isInternal = subscription?.is_internal === true;

  return (
    <Layout>
    <div className="p-6 max-w-4xl mx-auto space-y-8" data-testid="billing-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('billing.title')}</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">{t('billing.subtitle')}</p>
      </div>

      {/* Payment Status Banner */}
      {paymentStatus && (
        <div className={`p-4 rounded-xl flex items-center gap-3 ${
          paymentStatus === 'success' ? 'bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200' :
          paymentStatus === 'checking' ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-200' :
          'bg-red-50 dark:bg-red-900/30 border border-red-200'
        }`} data-testid="payment-status-banner">
          {paymentStatus === 'success' && <><CheckCircle className="w-5 h-5 text-emerald-600" /><span className="text-emerald-700 font-medium">{t('billing.paymentSuccess')}</span></>}
          {paymentStatus === 'checking' && <><RefreshCw className="w-5 h-5 text-blue-600 animate-spin" /><span className="text-blue-700 font-medium">{t('billing.verifyingPayment')}</span></>}
          {paymentStatus === 'cancelled' && <><AlertTriangle className="w-5 h-5 text-amber-600" /><span className="text-amber-700 font-medium">{t('billing.paymentCancelled')}</span></>}
          {(paymentStatus === 'error' || paymentStatus === 'expired' || paymentStatus === 'timeout') && <><AlertTriangle className="w-5 h-5 text-red-600" /><span className="text-red-700 font-medium">{t('billing.paymentFailed')}</span></>}
        </div>
      )}

      {/* Internal Organization Banner */}
      {isInternal && (
        <div className="rounded-2xl p-6 border-2 border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20" data-testid="internal-org-banner">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-800/40 flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-emerald-800 dark:text-emerald-300">Internal Organization</h2>
              <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-0.5">
                No payment required. All features are unlocked with unlimited access. This organization is managed internally.
              </p>
            </div>
            <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-emerald-200 dark:bg-emerald-700 text-emerald-800 dark:text-emerald-100 text-sm font-semibold">
              Unlimited
            </span>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-4">
            <div className="bg-white dark:bg-slate-800/60 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-emerald-700">{subscription?.user_count || 0}</p>
              <p className="text-xs text-slate-500 mt-0.5">Active Users</p>
            </div>
            <div className="bg-white dark:bg-slate-800/60 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-emerald-700">Unlimited</p>
              <p className="text-xs text-slate-500 mt-0.5">Seat Limit</p>
            </div>
            <div className="bg-white dark:bg-slate-800/60 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-emerald-700">₹0</p>
              <p className="text-xs text-slate-500 mt-0.5">Monthly Cost</p>
            </div>
          </div>
        </div>
      )}

      {/* Trial/Subscription Status Card — hidden for internal orgs */}
      {!isInternal && (
      <div className={`rounded-2xl p-6 border ${
        isActive ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200' :
        isTrialActive ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200' :
        isFreePlan ? 'bg-slate-50 border-slate-200 dark:border-slate-700' :
        'bg-amber-50 dark:bg-amber-900/30 border-amber-200'
      }`} data-testid="subscription-status-card">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {isActive && <CheckCircle className="w-5 h-5 text-emerald-600" />}
              {isTrialActive && <Clock className="w-5 h-5 text-blue-600" />}
              {(isTrialExpired || isExpired) && <AlertTriangle className="w-5 h-5 text-amber-600" />}
              {isFreePlan && <CheckCircle className="w-5 h-5 text-slate-500 dark:text-slate-400" />}
              <h2 className={`text-lg font-semibold ${
                isActive ? 'text-emerald-700' :
                isTrialActive ? 'text-blue-700' :
                isFreePlan ? 'text-slate-700 dark:text-slate-200' :
                'text-amber-700'
              }`}>
                {isActive && (subscription?.plan_tier === 'premium' ? 'Premium Plan' : 'Business Plan')}
                {isFreePlan && 'Individual Plan'}
                {isTrialActive && t('billing.freeTrialActive')}
                {isTrialExpired && t('billing.trialExpired')}
                {isExpired && 'Subscription Expired'}
                {!subscription?.has_subscription && !isFreePlan && t('billing.noSubscription')}
              </h2>
              {isActive && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                  Active
                </span>
              )}
              {isFreePlan && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-slate-200 text-slate-600 dark:text-slate-300 text-xs font-semibold">
                  Free
                </span>
              )}
              {isExpired && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                  Expired
                </span>
              )}
            </div>
            <p className={`text-sm ${
              isActive ? 'text-emerald-600' : isTrialActive ? 'text-blue-600' : isFreePlan ? 'text-slate-500 dark:text-slate-400' : 'text-amber-600'
            }`}>
              {isActive && t('billing.validUntil', { date: new Date(subscription.current_period_end).toLocaleDateString(), cycle: subscription.billing_cycle || 'monthly' })}
              {isFreePlan && 'Free forever for solo use. Upgrade to Business for team features.'}
              {isTrialActive && t('billing.daysRemaining', { days: subscription.days_remaining })}
              {isTrialExpired && t('billing.subscribePrompt')}
              {isExpired && 'Your subscription has expired. Renew below to restore access to all features.'}
            </p>
            {(isActive || isTrialActive) && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {t('billing.activeUsersCount', { count: subscription?.user_count || 0 })}
                {subscription?.pending_invites > 0 && ` ${t('billing.pendingInvites', { count: subscription.pending_invites })}`}
              </p>
            )}
          </div>
          {isActive && (
            <div className="text-right">
              <div className="text-2xl font-bold text-emerald-700">
                ₹{subscription.billing_cycle === 'yearly' 
                  ? (subscription.billable_users || subscription.user_count) * Math.round(effectiveActivePrice * 12 * 0.9)
                  : (subscription.billable_users || subscription.user_count) * effectiveActivePrice}
              </div>
              <div className="text-sm text-emerald-600">
                {subscription.billing_cycle === 'yearly' ? '/year' : '/month'}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                ₹{effectiveActivePrice}/user/month &bull; {subscription.billable_users || subscription.user_count} seat(s)
              </div>
              {promoActiveOnPlan && (
                <div className="text-xs text-emerald-600 mt-0.5">
                  {subscription.promo_code} discount applied
                </div>
              )}
            </div>
          )}
        </div>

        {/* Trial User Limit Info */}
        {(isTrialActive || isTrialExpired) && (
          <div className="mt-4 flex items-start gap-3 p-3 bg-white dark:bg-slate-800/60 rounded-xl border border-blue-100" data-testid="trial-user-limit-info">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-slate-800">
                {t('billing.trialUserLimit', { limit: TRIAL_USER_LIMIT })}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {t('billing.currentUsers', { count: subscription?.user_count || 1 })}
                {isTrialActive && ` ${t('billing.canInviteMore', { count: TRIAL_USER_LIMIT - (subscription?.user_count || 1) })}`}
                {isTrialExpired && ` ${t('billing.subscribeForMore')}`}
              </p>
            </div>
          </div>
        )}

        {/* Promo discount info for trial users */}
        {(isTrialActive || isTrialExpired) && promoActiveOnPlan && promoExpiresAt && (
          <div className="mt-3 flex items-center gap-2 p-3 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl border border-emerald-100" data-testid="trial-promo-banner">
            <Sparkles className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <span className="text-sm text-emerald-700">
              <span className="font-semibold">{subscription.promo_code}</span> discount applied — ₹{effectiveActivePrice}/user/month until {promoExpiresAt.toLocaleDateString()}
            </span>
          </div>
        )}

        {/* Collapsible "What's included" for active Business Plan */}
        {isActive && (
          <div className="mt-4">
            {promoActiveOnPlan && promoExpiresAt && (
              <div className="mb-3 flex items-center gap-2 p-3 bg-emerald-50 dark:bg-emerald-900/30/80 rounded-xl border border-emerald-100" data-testid="promo-active-banner">
                <Sparkles className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <span className="text-sm text-emerald-700">
                  <span className="font-semibold">{subscription.promo_code}</span> launch discount active &mdash; ₹{effectiveActivePrice}/user/month until {promoExpiresAt.toLocaleDateString()}
                </span>
              </div>
            )}
            <button
              onClick={() => setShowPlanFeatures(!showPlanFeatures)}
              className="flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-800 transition-colors"
              data-testid="show-plan-features-btn"
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${showPlanFeatures ? 'rotate-180' : ''}`} />
              {showPlanFeatures ? 'Hide' : "What's included in your plan"}
            </button>
            {showPlanFeatures && (
              <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 p-4 bg-white dark:bg-slate-800/60 rounded-xl border border-emerald-100">
                {[
                  'Unlimited projects',
                  'Unlimited team members',
                  'Timesheet approvals',
                  'Detailed reports & exports',
                  'Calendar integrations',
                  'Leave management',
                  'Screenshot tracking',
                  'Client management',
                  'Invoicing',
                  'Priority email support',
                ].map((f) => (
                  <div key={f} className="flex items-center gap-2 py-0.5">
                    <Check className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                    <span className="text-sm text-slate-700 dark:text-slate-200">{f}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Manage Seats Section - Active subscriptions */}
      {isActive && !isInternal && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6" data-testid="manage-seats-section">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" />
            {t('billing.manageSeats')}
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                <div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">{t('billing.activeUsers')}</div>
                  <div className="text-xl font-bold text-slate-900 dark:text-white">{subscription.user_count}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">{t('billing.paidSeats')}</div>
                  <div className="text-xl font-bold text-blue-600">{subscription.billable_users || subscription.user_count}</div>
                </div>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('billing.seatsInfo')}
              </p>
            </div>
            <div className="space-y-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">{t('billing.numberOfSeats')}</label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSeatCount(prev => Math.max(1, (prev || subscription.billable_users || 1) - 1))}
                  className="w-10 h-10 rounded-lg border border-slate-300 dark:border-slate-600 flex items-center justify-center text-lg font-bold hover:bg-slate-100 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600"
                  data-testid="seat-decrease-btn"
                >-</button>
                <input
                  type="number" min="1"
                  value={seatCount ?? (subscription.billable_users || subscription.user_count)}
                  onChange={(e) => setSeatCount(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 h-10 text-center text-lg font-bold rounded-lg border border-slate-300 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  data-testid="seat-count-input"
                />
                <button
                  onClick={() => setSeatCount(prev => (prev || subscription.billable_users || 1) + 1)}
                  className="w-10 h-10 rounded-lg border border-slate-300 dark:border-slate-600 flex items-center justify-center text-lg font-bold hover:bg-slate-100 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600"
                  data-testid="seat-increase-btn"
                >+</button>
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-300">
                New cost: <span className="font-bold text-slate-900 dark:text-white">
                  ₹{getDisplayPrice(seatCount ?? (subscription.billable_users || subscription.user_count))}{getCycleLabel()}
                </span>
              </div>
              {(seatCount ?? 0) !== (subscription.billable_users || subscription.user_count) && seatCount !== null && (
                <>
                  {seatCount > (subscription.billable_users || subscription.user_count) ? (
                    (() => {
                      const currentPaid = subscription.billable_users || subscription.user_count;
                      const additional = seatCount - currentPaid;
                      const additionalCost = billingCycle === 'yearly' ? additional * Math.round(effectiveActivePrice * 12 * 0.9) : additional * effectiveActivePrice;
                      return showSeatPayment ? (
                        <div className="space-y-3 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 rounded-xl">
                          <p className="text-sm font-medium text-blue-800">
                            Pay ₹{additionalCost} for {additional} additional seat(s) ({billingCycle})
                          </p>
                          <div className="flex gap-2">
                            <Button onClick={() => handleRazorpayCheckout(seatCount)} disabled={paymentLoading}
                              className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm" data-testid="seat-pay-btn">
                              {paymentLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CreditCard className="w-4 h-4 mr-1" />}
                              Pay Now
                            </Button>
                          </div>
                          <button onClick={() => setShowSeatPayment(false)} className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-200 underline" data-testid="cancel-seat-payment-btn">Cancel</button>
                        </div>
                      ) : (
                        <Button onClick={handleUpdateSeats} className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-xl" data-testid="update-seats-btn">
                          <CreditCard className="w-4 h-4 mr-2" />
                          Add {additional} seat(s) — Pay ₹{additionalCost}
                        </Button>
                      );
                    })()
                  ) : (
                    <Button onClick={handleUpdateSeats} disabled={seatUpdateLoading}
                      className="w-full h-10 bg-slate-700 hover:bg-slate-800 text-white rounded-xl" data-testid="reduce-seats-btn">
                      {seatUpdateLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Reduce to {seatCount} seat(s) — ₹{getDisplayPrice(seatCount)}{getCycleLabel()}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pricing & Payment Section — hidden for internal orgs */}
      {!isActive && !isInternal && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6" data-testid="subscribe-section">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-6">{t('billing.subscribeTo')}</h3>
          
          {/* Billing Cycle Toggle */}
          <div className="flex items-center justify-center mb-8" data-testid="billing-cycle-toggle">
            <div className="inline-flex items-center p-1 bg-slate-100 dark:bg-slate-700 rounded-xl">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-200'
                }`}
                data-testid="billing-monthly-btn"
              >
                {t('billing.monthly')}
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                  billingCycle === 'yearly'
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-200'
                }`}
                data-testid="billing-yearly-btn"
              >
                {t('billing.yearly')}
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold">
                  {t('billing.save', { percent: DISCOUNT_PCT })}
                </span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Pricing Summary */}
            <div className="space-y-4">
              {/* Seat Selector */}
              <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl" data-testid="seat-selector">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    <span className="font-semibold text-slate-900 dark:text-white text-sm">Number of Seats</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setDesiredSeats(Math.max(subscription?.user_count || 1, (desiredSeats || 1) - 1))}
                    className="w-9 h-9 rounded-lg bg-white dark:bg-slate-600 border border-slate-200 dark:border-slate-500 flex items-center justify-center text-slate-600 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-500 transition-colors font-bold"
                    data-testid="seat-decrement-btn"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min={subscription?.user_count || 1}
                    value={desiredSeats || 1}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setDesiredSeats(Math.max(subscription?.user_count || 1, val));
                    }}
                    className="w-16 h-9 text-center rounded-lg border border-slate-200 dark:border-slate-500 bg-white dark:bg-slate-600 text-slate-900 dark:text-white font-semibold text-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    data-testid="seat-count-input"
                  />
                  <button
                    onClick={() => setDesiredSeats((desiredSeats || 1) + 1)}
                    className="w-9 h-9 rounded-lg bg-white dark:bg-slate-600 border border-slate-200 dark:border-slate-500 flex items-center justify-center text-slate-600 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-500 transition-colors font-bold"
                    data-testid="seat-increment-btn"
                  >
                    +
                  </button>
                </div>
                {(subscription?.user_count || 1) > 1 && (
                  <p className="text-xs text-slate-400 mt-2">Minimum {subscription.user_count} seats (current team size)</p>
                )}
              </div>

              {/* Price per user */}
              <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                <IndianRupee className="w-8 h-8 text-emerald-500" />
                <div>
                  {billingCycle === 'yearly' ? (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 dark:text-white">₹{Math.round(effectivePrice * 12 * 0.9)}/user/year</span>
                        {promoActiveOnPlan && <span className="text-xs line-through text-slate-400">₹{Math.round(PRICE_PER_USER * 12 * 0.9)}/year</span>}
                      </div>
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        Total: ₹{(desiredSeats || 1) * Math.round(effectivePrice * 12 * 0.9)}/year
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 dark:text-white">₹{effectivePrice}/user/month</span>
                        {promoActiveOnPlan && <span className="text-xs line-through text-slate-400">₹{PRICE_PER_USER}/user/month</span>}
                      </div>
                      <div className="text-sm text-slate-500 dark:text-slate-400">Total: ₹{(desiredSeats || 1) * effectivePrice}/month</div>
                    </>
                  )}
                </div>
              </div>


              <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                <Shield className="w-4 h-4" />
                <span>{t('billing.securePayment')}</span>
              </div>
            </div>

            {/* Payment Option */}
            <div className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">Choose your preferred payment method:</p>

              <Button
                onClick={() => handleRazorpayCheckout(desiredSeats)}
                disabled={paymentLoading}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                data-testid="pay-now-btn"
              >
                {paymentLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CreditCard className="w-5 h-5 mr-2" />}
                Pay Now
              </Button>

              <p className="text-xs text-slate-400 text-center">
                UPI, Net Banking, Credit/Debit Cards accepted
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade to Premium Section — hidden for internal orgs */}
      {isActive && !isInternal && (
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 text-white" data-testid="upgrade-premium-section">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Crown className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Upgrade to Premium</h3>
                <p className="text-slate-400 text-sm">For enterprises &amp; large teams</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-baseline gap-1">
                <span className="text-slate-500 dark:text-slate-400 line-through text-sm">₹300</span>
                <span className="text-2xl font-bold text-white">₹150</span>
              </div>
              <span className="text-slate-400 text-xs">/user/month</span>
              <div className="mt-1">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-400 text-[10px] font-bold">
                  50% OFF — Launch Offer
                </span>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="space-y-2.5">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Everything in Business, plus</p>
              {[
                'Unlimited users',
                'Custom domain',
                'Custom email notifications',
                'Region-based data storage',
                'Custom branding & white-label',
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-emerald-400" />
                  </div>
                  <span className="text-sm text-slate-300">{feature}</span>
                </div>
              ))}
            </div>
            <div className="space-y-2.5">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">&nbsp;</p>
              {[
                'Priority support with SLA',
                'Dedicated storage configurations',
                'Advanced analytics & insights',
                'API access',
                'SSO / SAML integration',
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-emerald-400" />
                  </div>
                  <span className="text-sm text-slate-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 pt-4 border-t border-slate-700">
            <a
              href="mailto:support@inspiroworks.com?subject=Upgrade%20to%20Premium%20Plan&body=Hi%2C%0A%0AI%20would%20like%20to%20upgrade%20my%20Inspiro Tracker%20account%20to%20the%20Premium%20plan.%0A%0AOrganization%3A%20%0ANumber%20of%20users%3A%20%0A%0APlease%20share%20the%20next%20steps.%0A%0AThank%20you"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-xl transition-all text-sm"
              data-testid="upgrade-contact-btn"
            >
              <Mail className="w-4 h-4" />
              Contact Support to Upgrade
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              or email <a href="mailto:support@inspiroworks.com" className="text-amber-400 hover:text-amber-300 underline">support@inspiroworks.com</a>
            </span>
          </div>
        </div>
      )}

      {/* Downgrade to Free Plan */}
      {(isActive || isExpired) && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6" data-testid="downgrade-section">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Change Plan</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Switch to the free Individual plan with limited features</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="text-slate-600 dark:text-slate-300 border-slate-300 hover:bg-slate-50 dark:bg-slate-700"
              onClick={() => { setDowngradeError(null); setShowDowngradeModal(true); }}
              data-testid="downgrade-btn"
            >
              Downgrade to Free Plan
            </Button>
          </div>
        </div>
      )}

      {/* Downgrade Confirmation Modal */}
      {showDowngradeModal && (
        <Dialog open={showDowngradeModal} onOpenChange={setShowDowngradeModal}>
          <DialogContent className="max-w-md" data-testid="downgrade-modal">
            <DialogHeader>
              <DialogTitle className="text-lg">Downgrade to Free Plan?</DialogTitle>
              <DialogDescription className="text-sm text-slate-500 dark:text-slate-400 mt-2 space-y-3">
                <p>Switching to the free Individual plan means you will lose access to:</p>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600 dark:text-slate-300">
                  <li>Team members &amp; employee management</li>
                  <li>Timesheet approvals</li>
                  <li>Detailed reports &amp; exports</li>
                  <li>Calendar integrations</li>
                  <li>Leave management</li>
                  <li>Screenshot tracking</li>
                  <li>Invoicing</li>
                  <li>Priority email support</li>
                </ul>
                <div className="p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 rounded-lg">
                  <p className="text-xs font-semibold text-amber-800">Before you downgrade:</p>
                  <p className="text-xs text-amber-700 mt-1">You must deactivate all other users first. Only 1 active user (you) should remain. Go to <strong>People</strong> to deactivate team members.</p>
                </div>
                {downgradeError && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 rounded-lg">
                    <p className="text-xs text-red-700 font-medium">{downgradeError}</p>
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 mt-2">
              <Button variant="outline" onClick={() => setShowDowngradeModal(false)} disabled={downgradeLoading} data-testid="cancel-downgrade-btn">
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDowngradeToFree}
                disabled={downgradeLoading}
                data-testid="confirm-downgrade-btn"
              >
                {downgradeLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirm Downgrade
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Payment History */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6" data-testid="payment-history">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <Receipt className="w-5 h-5" />
          {t('billing.paymentHistory')}
        </h3>

        {transactions.length === 0 ? (
          <p className="text-slate-500 dark:text-slate-400 text-center py-8">{t('billing.noPaymentHistory')}</p>
        ) : (
          <div className="space-y-3">
            {transactions.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    tx.status === 'paid' ? 'bg-emerald-100 dark:bg-emerald-900/40' : tx.status === 'failed' ? 'bg-red-100 dark:bg-red-900/40' : 'bg-slate-200 dark:bg-slate-600'
                  }`}>
                    {tx.status === 'paid' ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <CreditCard className="w-5 h-5 text-slate-500 dark:text-slate-400" />}
                  </div>
                  <div>
                    <div className="font-medium text-slate-900 dark:text-white">₹{(tx.amount / 100).toFixed(2)}</div>
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {tx.provider.charAt(0).toUpperCase() + tx.provider.slice(1)} &bull; {tx.user_count} user(s)
                      {tx.metadata?.billing_cycle === 'yearly' && <span className="text-emerald-600 ml-1">&bull; Annual</span>}
                    </div>
                  </div>
                </div>
                <div className="text-right flex items-center gap-3">
                  {tx.status === 'paid' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/invoice/${tx.id}`)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:bg-blue-900/30 h-8 px-2"
                      data-testid={`view-invoice-btn-${tx.id}`}
                    >
                      <FileText className="w-4 h-4 mr-1" /> Invoice
                    </Button>
                  )}
                  <div>
                    <div className={`text-sm font-medium ${
                      tx.status === 'paid' ? 'text-emerald-600' : tx.status === 'failed' ? 'text-red-600' : 'text-slate-500 dark:text-slate-400'
                    }`}>
                      {tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                    </div>
                    <div className="text-xs text-slate-400">{new Date(tx.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default BillingPage;
